import { useState, useEffect } from 'react';

import {
  Direction,
  EntityCatalogGraphCard,
  EntityEdgeData,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { DevHubAlert, DevHubBox, DevHubButton } from '@devxdiscover/devhub-ui';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import {
  ConfigHelper,
  Feature,
  getApplicationDeps,
} from '@devxdiscover/devhub-common';
import { DependencyGraphTypes } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';

enum DiagramState {
  DependencyDiagram = 'dependencyDiagram',
  SystemDiagram = 'systemDiagram',
}

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

/**
 * DependencyDiagram component to display the dependency diagram and system diagram
 */
const DependencyDiagram = () => {
  const isDependencyDiagramEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.DEPENDENCY_DIAGRAM,
  );

  const [diagramState, setDiagramState] = useState<DiagramState>(
    isDependencyDiagramEnabled
      ? DiagramState.DependencyDiagram
      : DiagramState.SystemDiagram,
  );
  const navigate = useNavigate();
  const params = useQuery();
  const diagramKind = params.get('kind');

  useEffect(() => {
    if (diagramKind === DiagramState.DependencyDiagram) {
      setDiagramState(DiagramState.DependencyDiagram);
    }
  }, [diagramKind]);

  const handleButtonClick = (value: DiagramState) => {
    setDiagramState(value);
    navigate(`${window.location.pathname}?kind=${value}`);
  };

  return (
    <>
      <ConfigFeatureCheck feature={Feature.DEPENDENCY_DIAGRAM}>
        <DevHubBox display="flex" alignItems="center">
          <DevHubButton
            height="large"
            variant={
              diagramState === DiagramState.DependencyDiagram
                ? 'contained'
                : 'outlined'
            }
            onClick={() => handleButtonClick(DiagramState.DependencyDiagram)}
            aria-label="Dependency Diagram"
          >
            Dependency Diagram
          </DevHubButton>
          <DevHubButton
            height="large"
            variant={
              diagramState === DiagramState.SystemDiagram
                ? 'contained'
                : 'outlined'
            }
            onClick={() => handleButtonClick(DiagramState.SystemDiagram)}
            aria-label="System Diagram"
          >
            System Diagram
          </DevHubButton>
          {diagramState === DiagramState.DependencyDiagram && (
            <DevHubAlert severity="info">
              Nodes on the right are dependencies of the nodes on the left.
            </DevHubAlert>
          )}
        </DevHubBox>
      </ConfigFeatureCheck>
      <DevHubBox tbPadding="large">
        <ConfigFeatureCheck feature={Feature.DEPENDENCY_DIAGRAM}>
          {diagramState === DiagramState.DependencyDiagram && (
            <>
              <EntityCatalogGraphCard
                variant="gridItem"
                title="Dependency Diagram"
                height={800}
                showArrowHeads
                relations={[RELATION_DEPENDENCY_OF, RELATION_DEPENDS_ON]}
                renderLabel={element => (
                  <DefaultRenderLabel edge={element.edge} />
                )}
                unidirectional
              />
            </>
          )}
        </ConfigFeatureCheck>
        {diagramState === DiagramState.SystemDiagram && (
          <EntityCatalogGraphCard
            variant="gridItem"
            direction={Direction.TOP_BOTTOM}
            title="System Diagram"
            height={800}
            relations={[
              RELATION_PART_OF,
              RELATION_HAS_PART,
              RELATION_API_CONSUMED_BY,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_PROVIDES_API,
            ]}
            unidirectional={false}
          />
        )}
      </DevHubBox>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  text: {
    fill: theme.palette.textContrast,
  },
  secondary: {
    fill: theme.palette.textSubtle,
  },
}));

/**
 * DefaultRenderLabel component to render the label for the dependency diagram
 * @param edgeData - edge data
 */
export function DefaultRenderLabel(
  edgeData: DependencyGraphTypes.RenderLabelProps<EntityEdgeData>,
) {
  const classes = useStyles();
  const edge = edgeData.edge;
  const applicationDeps: string[] =
    getApplicationDeps(edge.from, edge.to) ?? [];
  return (
    <g>
      <text className={classes.text} textAnchor="middle" x="1rem" y="-2.5rem">
        {[edge.relations?.[0]].map(r => (
          <tspan key={r}>{r}</tspan>
        ))}
      </text>
      <text className={classes.text} textAnchor="middle" x="1rem" y="-0.75rem">
        [{applicationDeps.join(', ')}]
      </text>
    </g>
  );
}

export default DependencyDiagram;
